import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { PlatformsDropdown, PlatformsMobileSection } from "./PlatformsDropdown";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, ArrowRight } from "lucide-react";

const NAV_LINKS = [
  { id: "atlas",   labelKey: "nav.atlas",       href: "/#atlas" },
  { id: "sygnal",  labelKey: "nav.sygnalPage",  href: "/sygnal-shield", route: true },
  { id: "safelink",labelKey: "nav.safelinkPage",href: "/safelink", route: true },
  { id: "sectors", labelKey: "nav.sectors",     href: "/#sectors" },
  { id: "armor",   labelKey: "nav.armor",       href: "/#armor" },
  { id: "forensic",labelKey: "nav.forensic",    href: "/#forensic" },
];

export const Navbar = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isAuth = user && typeof user === "object";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#050505]/80 border-b border-white/[0.08]"
      data-testid="public-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-link">
          <Logo size={28} />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((l) =>
            l.route ? (
              <Link
                key={l.id}
                to={l.href}
                data-testid={`nav-link-${l.id}`}
                className="text-sm text-white/70 hover:text-[#19A7E0] transition-colors"
              >
                {t(l.labelKey)}
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-link-${l.id}`}
                className="text-sm text-white/70 hover:text-white transition-colors"
              >
                {t(l.labelKey)}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <PlatformsDropdown />
          {isAuth ? (
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="nav-dashboard-btn"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 btn-primary text-xs font-mono uppercase tracking-[0.18em] rounded-sm"
            >
              {t("nav.dashboard")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              data-testid="nav-login-btn"
              className="inline-flex items-center gap-2 px-4 py-2 btn-primary text-xs font-mono uppercase tracking-[0.18em] rounded-sm"
            >
              {t("nav.login")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 text-white/80"
            data-testid="nav-mobile-toggle"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#050505]" data-testid="mobile-nav-panel">
          <div className="px-6 py-4 space-y-3">
            <PlatformsMobileSection onLinkClick={() => setOpen(false)} />
            {NAV_LINKS.map((l) =>
              l.route ? (
                <Link
                  key={l.id}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="block text-sm text-[#19A7E0] py-2"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {t(l.labelKey)}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block text-sm text-white/80 py-2"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {t(l.labelKey)}
                </a>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
